import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { SITE } from "@/lib/site";
import { BLOG_PATH } from "@/lib/blogConfig";

const NAV_LINKS = [
  { label: "About", href: "/#about" },
  { label: "Free Guide", href: "/#guide" },
  { label: "The Area", href: "/#area" },
  { label: "Services", href: "/#services" },
  { label: "Q&A", href: "/faq" },
  { label: "Blog", href: BLOG_PATH },
  { label: "Contact", href: "/#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-bone/80 border-b border-border2/60"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className={`font-display text-xl md:text-2xl tracking-tight transition-colors duration-300 ${
            scrolled ? "text-ink" : "text-white"
          }`}
        >
          J<span className="text-brass">.</span>J<span className="text-brass">.</span> Botelho
        </a>

        <ul className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                target={l.external ? "_blank" : undefined}
                rel={l.external ? "noopener noreferrer" : undefined}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}-link`}
                className={`text-sm transition-colors duration-200 relative group ${
                  scrolled
                    ? "text-text2 hover:text-ink"
                    : "text-white/90 hover:text-white drop-shadow"
                }`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-brass transition-all duration-300 group-hover:w-full" />
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <a
            href={SITE.homesAgentUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-listings-btn"
            className={`hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 border ${
              scrolled
                ? "border-ink text-ink hover:bg-ink hover:text-white"
                : "border-white/60 text-white hover:bg-white hover:text-ink"
            }`}
          >
            Active Listings
          </a>
          <a
            href={SITE.mortgageApplyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-apply-btn"
            className={`hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 ${
              scrolled
                ? "bg-ink text-white hover:bg-slate2"
                : "bg-brass text-ink hover:bg-white"
            }`}
          >
            Apply Now
          </a>
          <a
            href="/#contact"
            data-testid="nav-contact-btn"
            className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 ${
              scrolled
                ? "bg-brass text-ink hover:bg-ink hover:text-white"
                : "bg-white text-ink hover:bg-brass"
            }`}
          >
            Contact J.J.
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            className={`md:hidden p-2 rounded-md transition-colors ${
              scrolled ? "text-ink hover:bg-sand" : "text-white hover:bg-white/10"
            }`}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-border2 bg-bone/95 backdrop-blur-xl"
        >
          <ul className="px-6 py-6 space-y-4">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  target={l.external ? "_blank" : undefined}
                  rel={l.external ? "noopener noreferrer" : undefined}
                  onClick={() => setOpen(false)}
                  className="block text-base text-text2 hover:text-ink"
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li className="pt-2 flex flex-col gap-3">
              <a
                href={SITE.homesAgentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex border border-ink text-ink px-5 py-2.5 rounded-full text-sm font-medium"
              >
                Active Listings
              </a>
              <a
                href={SITE.mortgageApplyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex bg-ink text-white px-5 py-2.5 rounded-full text-sm font-medium"
              >
                Apply Now
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
